import CardWrapper from '../wrapper/card';
import CompanyInfoModal from './modal/company_info';
import CreateProduct from './create_product';
import { currencyOptions } from '../../helper';
import CustomDropdown from '../reusable/dropdown';
import InvoicesTable from './invoices_table';
import { useParams } from 'react-router';
import { Add, Done, DoNotDisturbOnOutlined, HighlightOffOutlined, MoneyOutlined, Receipt } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { brand, green, orange } from '../../theme/themePrimitives';
import { CreateInvoiceParams, ProductObject } from '../../params/redux/product';
import { createInvoiceThunk, fetchAnalyticsThunk, fetchInvoicesThunk, fetchProductsThunk } from '../../redux/thunks/products';
import { settingCreatingProduct, settingSelectedProduct } from '../../redux/slices/product';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const BillingComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const { createProduct, products, selectedProduct, totalPaid, totalUnpaid, totalBill, invoices, loaders } = useSelector((state: RootState) => state.product);
    const filteredProductsForDropdown = [...products].map((item) => ({
        label: item.name,
        value: item.id
    }));
    const bills = [
        {
            id: 1,
            name: 'Total Bill',
            amount: `$${(totalBill).toFixed(2).toString()}`,
            currency: 'USD',
            icon: <MoneyOutlined />,
            color: 'success'
        },
        {
            id: 2,
            name: 'Paid Bill',
            amount: `$${totalPaid.toFixed(2).toString()}`,
            currency: 'USD',
            icon: <Done />,
            color: 'primary'
        },
        {
            id: 3,
            name: 'Unpaid Bill',
            amount: `$${totalUnpaid.toFixed(2).toString()}`,
            currency: 'USD',
            icon: <DoNotDisturbOnOutlined />,
            color: 'warning'
        }
    ];

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                await dispatch(fetchAnalyticsThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
                await dispatch(fetchInvoicesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
                await dispatch(fetchProductsThunk()).unwrap();
            } catch (e) {
                // Catch errors
            }
        };
        fetchProducts();
    }, []);


    const createInvoiceHandler = async () => {
        try {
            const data: CreateInvoiceParams = {
                client_id: id ? parseInt(id as string, 10) : 0,
                stripe_price_id: selectedProduct?.stripe_price_id || '',
                quantity: 1
            };
            await dispatch(createInvoiceThunk(data)).unwrap();
            dispatch(settingSelectedProduct(null));
        } catch (e) {
            // Catch errors
        }
    };

    if (createProduct) {
        return <CreateProduct />;
    }

    return (
        <CardWrapper>
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">
                        Active since <Typography component="span" variant="h5" color="primary">January, 2025</Typography>
                    </Typography>
                    <Box>
                        <CustomDropdown
                            options={currencyOptions}
                            value="usd"
                            onChange={() => null}
                        />
                    </Box>
                </Box>
                <Box display="flex" gap={2} mb={1}>
                    {bills.map((bill) => (
                        <Box key={bill.id} width="100%">
                            <CardWrapper>
                                <Box minHeight="120px" display="flex" justifyContent="start" gap={2} alignItems="center">
                                    <Box display="flex" alignItems="center" justifyContent="center" p={1} borderRadius="5px" bgcolor={
                                        bill.color === 'success' ? green[100] : bill.color === 'warning' ? orange[100] : brand[100]
                                    } color={bill.color === 'success' ? green[300] : bill.color === 'warning' ? orange[300] : brand[300]}>
                                        {bill.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" color="textDisabled">{bill.name}</Typography>
                                        <Typography variant="h5">{bill.currency} {bill.amount}</Typography>
                                    </Box>
                                </Box>
                            </CardWrapper>
                        </Box>
                    ))}
                </Box>
                <CardWrapper>
                    <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                        <Typography variant="h5" color="textDisabled">Products</Typography>
                        <Button variant="text" onClick={() => setShowModal(true)} disabled={loaders.isLoading || loaders.isCreating}>Company Info</Button>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box>
                                <CustomDropdown
                                    options={filteredProductsForDropdown}
                                    placeholder="Choose your product"
                                    value={selectedProduct ? selectedProduct.id : 'choose'}
                                    onChange={(event) => {
                                        const selected = products.find((p: ProductObject) => p.id === event.target.value);
                                        dispatch(settingSelectedProduct(selected || null));
                                    }}
                                    sx={{
                                        marginBottom: '0'
                                    }}
                                />
                            </Box>
                            {selectedProduct && (
                                <IconButton onClick={() => dispatch(settingSelectedProduct(null))}>
                                    <HighlightOffOutlined />
                                </IconButton>
                            )}

                            {/* Show "Create Invoice" only when a product is selected */}
                            {selectedProduct && (
                                <Button variant="contained" startIcon={<Receipt />} fullWidth onClick={createInvoiceHandler} disabled={loaders.isLoading || loaders.isCreating}>
                                    Send {selectedProduct && selectedProduct.billing_type === 'recurring' && 'Recurring'} Invoice
                                </Button>
                            )}
                        </Box>
                        <Button variant="contained" startIcon={<Add />} onClick={() => dispatch(settingCreatingProduct(true))} disabled={loaders.isLoading || loaders.isCreating}>Create New Product</Button>
                    </Box>
                </CardWrapper>
                <CardWrapper>
                    {
                        invoices.length === 0 ? <Box>No transactions</Box> : <InvoicesTable />
                    }
                </CardWrapper>
            </Box>
            {showModal && <CompanyInfoModal open={showModal} onClose={() => setShowModal(false)} />}
        </CardWrapper>
    );
};

export default BillingComponent;